import { useCallback, useState } from 'react';

import { v4 as uuidv4 } from 'uuid';

import { actionHelpers } from 'app-components/flux/core';
import {
  scheduledTransactionsActions,
  scheduledTransactionsSelectors,
} from 'app-components/flux/scheduled-transactions';
import type { CreateBillScoutScheduleMetaProps } from 'app-components/flux/scheduled-transactions/scheduledTransactionsActions';
import type { ScheduledTransaction } from 'app-components/flux/scheduled-transactions/scheduledTransactionsTypes';
import { withTimeout } from 'app-components/hooks/billScoutSetup';

import { useAppDispatch, useAppSelector } from 'hooks/redux';

// Mirrors Confirm.tsx's BATCH_TIMEOUT_MS: caps the wait so a swallowed or
// stuck request can't leave a caller's "creating" state spinning forever.
const CREATE_TIMEOUT_MS = 30000;

export type UseCreateBillScoutScheduleResult = {
  /** Dispatches `createBillScoutSchedule` for `transactionId`, starts tracking its response, and returns the generated client id. */
  create: (transactionId: string, props?: CreateBillScoutScheduleMetaProps) => string;
  /** The client id of the most recent `create` call, until `clearPending` is called. */
  pendingClientId: string | null;
  /** The created schedule once its response has reached the store — undefined while still pending. */
  pendingModel: ScheduledTransaction | undefined;
  /** Stops tracking the current pending create (e.g. once the caller has consumed `pendingModel`). */
  clearPending: () => void;
  /** True from `create()` until its request settles (success, failure, or timeout) — callers should block Finish/exit for the duration. */
  isCreating: boolean;
  /** True once the most recent `create()` request has failed or timed out; cleared by `clearPending`. */
  hasCreateFailed: boolean;
};

/**
 * Wraps "dispatch `createBillScoutSchedule` for a picked transaction, then
 * wait for the client-id-keyed row to land in the store" — the same Bill
 * Scout endpoint call shared by the Bill Setup Review screen and the Create
 * Series ("pick an existing transaction") flow.
 */
export function useCreateBillScoutSchedule(): UseCreateBillScoutScheduleResult {
  const dispatch = useAppDispatch();
  const [pendingClientId, setPendingClientId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [hasCreateFailed, setHasCreateFailed] = useState(false);
  const pendingModel = useAppSelector((state) =>
    pendingClientId
      ? scheduledTransactionsSelectors.getScheduledTransactionByClientId(state, pendingClientId)
      : undefined,
  );

  const create = useCallback(
    (transactionId: string, props?: CreateBillScoutScheduleMetaProps) => {
      const clientId = uuidv4().toUpperCase();
      setPendingClientId(clientId);
      setHasCreateFailed(false);
      setIsCreating(true);
      void withTimeout(
        actionHelpers.bindPromiseAction(dispatch, scheduledTransactionsActions.createBillScoutSchedule)(
          {
            clientId,
            id: transactionId,
            ...(props?.isUserVerified !== undefined && { schedule: { isUserVerified: props.isUserVerified } }),
          },
          { props },
        ),
        CREATE_TIMEOUT_MS,
        'bill scout create timed out',
      )
        .catch(() => setHasCreateFailed(true))
        .finally(() => setIsCreating(false));
      return clientId;
    },
    [dispatch],
  );

  const clearPending = useCallback(() => {
    setPendingClientId(null);
    setHasCreateFailed(false);
  }, []);

  return { create, pendingClientId, pendingModel, clearPending, isCreating, hasCreateFailed };
}
